import React, { useMemo } from 'react';
import {
  ColumnDef,
  HeaderGroup,
  SortingState,
  Table,
} from '@tanstack/react-table';
import { CSSProp } from 'styled-components';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { HeaderRow } from './common';
import DraggableColumnHeader from './DraggableColumnHeader';

export type Column<T> = ColumnDef<T, T> & { id: string };

interface TableHeadersProps<T> {
  table: Table<T>;
  sortSide: 'left' | 'right';
  order: Array<string>;
  sort?: SortingState;
  setOrder: React.Dispatch<React.SetStateAction<Array<string>>>;
  css?: CSSProp;
}

const TableHeaders = <T,>({
  sort,
  table,
  order,
  setOrder,
  css,
  sortSide,
  ...rest
}: TableHeadersProps<T>) => {
  const isMobile = useIsMobile();

  return (
    <>
      {useMemo(() => {
        return table.getHeaderGroups().map((headerGroup) => {
          const key = getHeaderGroupKey(
            sort !== undefined ? sort : [],
            headerGroup,
          );
          return (
            <HeaderRow {...rest} css={css} key={key}>
              {headerGroup.headers.map((header) => {
                const isHidden = header.column.columnDef.meta?.hidden === true;
                const hiddenMobile =
                  isMobile &&
                  header.column.columnDef.meta?.isMobileHeader !== true;

                const shouldHidden = hiddenMobile || isHidden;
                if (shouldHidden) {
                  return null;
                }
                return (
                  <DraggableColumnHeader<T, unknown>
                    sortSide={sortSide}
                    key={header.id}
                    header={header}
                    table={table}
                    columnOrder={order}
                    setColumnOrder={setOrder}
                  />
                );
              })}
            </HeaderRow>
          );
        });
      }, [css, isMobile, order, rest, setOrder, sort, sortSide, table])}
    </>
  );
};

export default TableHeaders;

function getHeaderGroupKey<T>(sort: SortingState, headerGroup: HeaderGroup<T>) {
  return `${sort.flat().join('-')}${headerGroup.id}`; //Use sort on key to force re-render on sort change
}
